// Centralized helpers for handling API errors returned by the backend.
//
// 🔴 THE WIRE SHAPE — verified against the server, do not guess:
//   Mowazaf.Server/Data/General/View Models/Error.cs  ->  { Code, Description, Path, Time, Details }
//   Mowazaf.Server/Data/General/View Models/Result.cs ->  { Payload, Status, Errors: Error[] }
// serialised camelCase as { code, description, path, time, details } inside `errors: []`.
//
// There is NO `message` field on a server `Error`. Reading `result.errors[0].message` yields
// `undefined` at runtime (and a TS error where the model is typed), so the caller silently falls
// through to its generic fallback and the server's actual refusal reason never reaches the user.
// This has been fixed one file at a time before and keeps coming back — route every read through
// `getApiErrorMessage`/`getErrorMessages` instead of hand-rolling field access.
//
// `message` DOES exist on non-`Result` shapes (a thrown JS `Error`, an axios/network error, an
// ASP.NET ProblemDetails body). Those are handled below, which is why `ApiError.message` is
// declared as optional here — it is a foreign-shape escape hatch, never the `Result` contract.

import { apiErrorsOf } from './requestHandling';
import { localizeServerErrorCode, localizeServerErrorDescription } from './serverErrorCodeKeys';

export type ApiError = {
  code?: string;
  description?: string;
  path?: string;
  time?: string;
  /** Optional structured payload the server may attach (e.g. a conflict list). */
  details?: unknown;
  /**
   * NOT part of the server `Result<T>` error contract. Present only on foreign error shapes
   * (thrown JS `Error`, axios error, ProblemDetails). Kept last in the fallback order.
   */
  message?: string;
};

/**
 * The `description` defaults baked into `Error.cs`'s static factories. When the server returns one
 * of these verbatim it has told us nothing the caller did not already know, AND it is a hardcoded
 * English literal that is never localized. In that case the caller's own (translated) fallback is
 * strictly better copy for an ar/fr user, so these lose to it.
 *
 * Anything else in `description` is a deliberate, situation-specific message the server author
 * wrote for this exact refusal — that always wins.
 */
const GENERIC_SERVER_DESCRIPTIONS = new Set(
  [
    'Resource not found',
    'Invalid parameter',
    'You do not have access',
    'A conflict occured',
    'The data provided is not acceptable to process the request',
    'Bad request',
    'Something went wrong',
    'Something went wrong, please contact system administrator',
    'Invalid input data',
    'An error occurred',
    'Internal server error',
  ].map((s) => s.toLowerCase())
);

function isGenericDescription(description?: string): boolean {
  if (!description) return true;
  const normalised = description.trim().replace(/[.!]+$/, '').toLowerCase();
  return !normalised || GENERIC_SERVER_DESCRIPTIONS.has(normalised);
}

// Extract Error[] from common error shapes (Axios, fetch wrappers, raw throws, a bare Result).
//
// Delegates first to `apiErrorsOf` (utils/requestHandling), which is the CO-226 extractor: it walks
// the rejection wrappers AND reads the non-enumerable `__apiErrors` stamp the response interceptor
// leaves on rejected 4xx bodies. Without that step a structured refusal that arrived through a
// `catch` is invisible here. The hand-rolled walk below stays as the fallback for shapes that never
// went through the interceptor.
export function extractApiErrors(err: any): ApiError[] {
  const stamped = apiErrorsOf(err);
  if (stamped.length) return stamped as unknown as ApiError[];

  const from =
    err?.errors ||
    err?.payload?.errors ||
    err?.response?.data?.errors ||
    err?.response?.errors ||
    err?.data?.errors;

  if (Array.isArray(from) && from.length) return from;

  // Single error object fallbacks
  const single = err?.response?.data || err?.data || err;
  if (single?.code || single?.description) return [single];
  return [];
}

/**
 * Localized copy for the generic `Error.cs` codes, used only when the server description was
 * boilerplate AND the caller supplied no fallback of its own.
 */
function genericMessageForCode(code: string, t: (key: string, opts?: any) => string): string | '' {
  switch (code) {
    case 'NotFound':
      return t('The requested resource was not found. Please refresh and try again.');
    case 'PermissionDenied':
      return t("You don't have permission to perform this action.");
    case 'Conflict':
      return t('Your changes conflict with another update. Please refresh and try again.');
    case 'InternalServerError':
      return t('Server error while saving. Please try again in a moment.');
    case 'InvalidParameter':
    case 'BadRequest':
      return t('Some of the entered data is invalid. Please review and try again.');
    default:
      return '';
  }
}

/**
 * Turn a `Result`/axios/thrown error into localized, user-facing strings — ONE PER VIOLATION.
 *
 * 🔴 FALLBACK ORDER, per error entry (this is the documented contract):
 *   0. `SERVER_ERROR_CODE_KEYS[code]` — the EXPLICIT, enumerated code → locale-key table
 *      (`utils/serverErrorCodeKeys.ts`). It runs FIRST, ahead of the `errors:` seam below, because
 *      an enumerated mapping somebody wrote on purpose must not be beatable by an accidental
 *      namespace-fallback hit (see step 1's note, and the file's own header for the measurement).
 *   1. `t('errors:<code>')` — a client-side override, IF that key actually resolves.
 *   2. `description` — the server's own text, when it is situation-specific (not `Error.cs`
 *      boilerplate). This is the default and it is deliberate: several server paths already
 *      return culture-correct text (Identity password rules via `LocalizedIdentityErrorDescriber`
 *      honour `?culture=ar|fr`), and the codes are NOT 1:1 with messages — `BadRequest` alone
 *      carries ~50 distinct descriptions — so a code->key table would COLLAPSE them into one
 *      generic string and be strictly worse than what the server already sends.
 *   3. `message` — foreign shapes only (thrown JS Error / axios / ProblemDetails).
 *   4. the caller's `fallback` — already translated at the call site.
 *   5. localized generic copy chosen from the error `code`.
 *   6. `t('common:Something went wrong')`.
 *
 * Returns EVERY error in the array, not just `[0]`: a single request can fail several rules at
 * once (an Identity password can violate up to three), and showing one, having it fixed, then
 * refusing again on the next is a bad loop.
 */
export function getErrorMessages(
  err: any,
  t: (key: string, opts?: any) => string,
  fallback?: string
): string[] {
  const errors = extractApiErrors(err);
  if (errors.length) {
    return errors.map((e) => {
      const code = e.code ?? '';

      // Step 0 — the enumerated table. Returns '' (never the code, never the key) when this build
      // does not know the code, so an unknown code simply continues down the chain.
      const mapped = localizeServerErrorCode(code, t);
      if (mapped) return mapped;

      const key = code ? `errors:${code}` : '';
      // `defaultValue: ''` so a miss comes back EMPTY instead of echoing the code — that removes the
      // only path by which step 1 could ever paint a raw `ASSIGNMENT_MATCH_CLOSED` at a user.
      const localized = key ? t(key, { defaultValue: '' }) : '';
      // Robust missing-translation detection:
      // i18next may return the key ("errors:PermissionDenied") OR the raw code ("PermissionDenied") when a key is missing,
      // depending on fallbackNS/defaultNS config. Treat both as missing and fall back to backend description.
      //
      // ⚠️ CORRECTION, 2026-09-21 (MEASURED against the real bundles with i18next 22.5.1 — the older
      // note here claimed this seam "never fires", which is true only for codes that are not keys
      // anywhere). `i18n.js` sets `fallbackNS: ['common','translation','tournament']`, and fallbackNS
      // DOES resolve a lookup whose primary namespace is not registered at all: on an Arabic page
      // `t('errors:Expired')` already returns "منتهي الصلاحية", the `common` bundle's word — and
      // `Expired`/`Pending`/`Active`/`Required`/`Success`/`Duplicate`/`Error` are all live keys
      // there. A short server code that collides with a UI word therefore renders that ONE WORD as
      // the whole refusal. Per-call `fallbackNS: []`/`false` does not suppress it (probed; i18next
      // ignores the override), which is why step 0 above exists and runs first, and why no `errors`
      // namespace was registered — doing so would promote this wildcard to the primary path.
      const looksLikeMissing = !localized || localized === key || (!!code && localized === String(code));
      if (!looksLikeMissing) return localized;

      // A description that is just the code repeated is a raw code with extra steps: never show it.
      const description = typeof e.description === 'string' ? e.description.trim() : '';
      const descriptionIsCode = !!code && description === code;

      // Step 2a (2026-09-28) — an ALLOWLISTED fixed sentence is itself a translation.json key (the same key
      // mobile resolves). Anything not on the list keeps step 2's behaviour byte for byte.
      const localizedDescription = localizeServerErrorDescription(description, t);
      if (localizedDescription) return localizedDescription;

      if (!descriptionIsCode && !isGenericDescription(e.description)) return e.description as string;
      if (e.message && String(e.message).trim()) return String(e.message);
      if (fallback) return fallback;

      const generic = genericMessageForCode(code, t);
      if (generic) return generic;

      // Last resort: a boilerplate English description still beats nothing.
      return (!descriptionIsCode && e.description) || t('common:Something went wrong');
    });
  }

  // No structured backend errors were found. Infer a helpful message from common HTTP/network shapes.
  const status: number | undefined =
    err?.response?.status ??
    err?.status ??
    err?.response?.data?.status;

  const rawMessage: string | undefined =
    err?.response?.data?.message ||
    err?.response?.data?.title ||
    err?.message;

  const hasResponse = !!err?.response;
  const isNetwork = !hasResponse && (!!err?.request || String(rawMessage).toLowerCase().includes('network'));

  if (isNetwork) {
    return [
      fallback ||
        t('Could not reach the server. Please check your internet connection and try again.'),
    ];
  }

  if (typeof status === 'number') {
    switch (status) {
      case 400:
        return [fallback || rawMessage || t('Some of the entered data is invalid. Please review and try again.')];
      case 401:
        return [t('Your session has expired. Please sign in again and retry.')];
      case 403:
        return [t("You don't have permission to perform this action.")];
      case 404:
        return [t('The requested resource was not found. Please refresh and try again.')];
      case 409:
        return [t('Your changes conflict with another update. Please refresh and try again.')];
      default:
        if (status >= 500) {
          return [t('Server error while saving. Please try again in a moment.')];
        }
        break;
    }
  }

  if (rawMessage && typeof rawMessage === 'string' && rawMessage.trim()) {
    return [rawMessage];
  }

  return [fallback || t('common:Something went wrong')];
}

/**
 * THE ONE ACCESSOR CALL SITES SHOULD USE.
 *
 * Replaces the broken `result.errors[0]?.message || t('...')` idiom. Takes the same three things
 * the idiom had (the result/error, `t`, and the caller's translated fallback) and returns a single
 * ready-to-render string that carries EVERY violation the server reported, de-duplicated.
 *
 *   showToast(getApiErrorMessage(result, t, t('Submission failed')), 'error');
 *
 * `separator` defaults to a newline; pass `' '` for surfaces that collapse whitespace.
 */
export function getApiErrorMessage(
  err: any,
  t: (key: string, opts?: any) => string,
  fallback?: string,
  separator = '\n'
): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of getErrorMessages(err, t, fallback)) {
    const msg = (raw ?? '').toString().trim();
    if (!msg || seen.has(msg)) continue;
    seen.add(msg);
    out.push(msg);
  }
  if (!out.length) return fallback || t('common:Something went wrong');
  return out.join(separator);
}

// Convenience for snackbars (matches current snackbar severity union in the app)
export function showApiError(
  setSnackbar: (s: { open: boolean; message: string; severity: 'success' | 'error' | 'info' }) => void,
  t: (key: string, opts?: any) => string,
  err: any,
  fallback?: string
) {
  const msg = getErrorMessages(err, t, fallback).join('\n');
  setSnackbar({ open: true, message: msg, severity: 'error' });
}
